import React, { useCallback, useEffect, useRef, useState } from "react";
import Seo from "@/components/Seo";
import { SEO } from "@/lib/seoConfig";
import { Section, Kicker, SerifTitle, HairlineBrass } from "@/components/Section";
import { MediaGallery } from "@/components/MediaGallery";
import { VideoGallerySection } from "@/components/VideoGallerySection";
import { HeroVideo, heroTextShadow } from "@/components/HeroVideo";
import { SelectedClientWork } from "@/components/SelectedClientWork";
import { TestimonialCard } from "@/components/TestimonialCard";
import { useBusinessSelectedWork, useBusinessVideo, useTestimonials } from "@/lib/content";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Check, ArrowDown, ArrowUpRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { PublicAPI } from "@/lib/api";
import { cn } from "@/lib/utils";

// ===========================================================
// PAGE: Business Photography Arizona (/business)
// Lead-capture landing page targeting Arizona service businesses.
// ===========================================================

const PAGE_TITLE = "Arizona Brand & Event Photography | Elizaveta von Stuben";
const PAGE_DESCRIPTION =
    "Professional photography and video for Scottsdale and Phoenix hospitality, medical, events, and growing businesses. Summer special pricing available.";

const URGENCY_LINE =
    "Summer promo available through June 18, 2026.";

const BUYER_CARDS = [
    { id: "founders",    title: "Founders & personal brands",       copy: "Profile imagery and launch visuals for the way you want to be seen." },
    { id: "creatives",   title: "Creatives & consultants",          copy: "Editorial content that makes your work feel inevitable." },
    { id: "aesthetic",   title: "Aesthetic clinics & med spas",     copy: "Trust-building visuals patients believe before they book." },
    { id: "wellness",    title: "Wellness studios",                 copy: "Lifestyle and team content that fills classes." },
    { id: "legal",       title: "Lawyers & professional offices",   copy: "Polished portraits that command rate cards." },
    { id: "boutique",    title: "Boutique local brands",            copy: "Product, lifestyle, and brand content built to convert." },
    { id: "hospitality", title: "Hotels, resorts & spas",           copy: "Summer editorial that sells rooms, dining, and spa." },
    { id: "events",      title: "Conferences & corporate events",   copy: "Coverage that produces a month of content after the doors close." },
];

const BENEFITS = [
    "Images you feel proud to send people to",
    "A sharper visual direction for your website and socials",
    "Content that makes your work easier to understand and choose",
    "Weeks of content from one focused shoot",
    "Confidence in how you and your brand are perceived",
];

const PACKAGES = [
    {
        id: "business-refresh",
        name: "Business Refresh",
        promo: "$650",
        regular: "$850",
        bestFor: "Founders, consultants, and small business owners ready for a clean, immediate visual update.",
        includes: [
            "75–90 minute photo session",
            "1 location",
            "1 person",
            "Light planning and shot list",
            "Guided posing",
            "15 edited images",
            "5 social / web crops",
            "Website and social media usage",
            "Delivery in 7 business days",
        ],
        cta: "Request Business Refresh",
        inquiryType: "Summer Headshot + Brand Refresh",
    },
    {
        id: "brand-content-day",
        name: "Brand Content Day",
        promo: "$1,250",
        regular: "$1,650",
        badge: "Most Popular",
        bestFor: "Personal brands, creatives, clinics, wellness spaces, and teams ready for a strong photo library plus one polished social video.",
        includes: [
            "3‑hour photo session",
            "1 location or 2 nearby locations",
            "Up to 4 people",
            "Planning call and shot list",
            "Founder / team portraits",
            "Workspace / interior images",
            "Service / process / lifestyle images",
            "Detail photos for website and social",
            "35 edited images",
            "15 social / web crops",
            "1 edited vertical video, up to 30 seconds",
            "Website, social media, and light ad usage",
            "Photo delivery in 10 business days",
            "Video delivery in 14 business days",
        ],
        cta: "Request Brand Content Day",
        inquiryType: "Summer Brand Content Day",
    },
    {
        id: "brand-content-video",
        name: "Brand Content + Video",
        promo: "$1,850",
        regular: "$2,300",
        bestFor: "Founders, personal brands, and growing businesses building a full photo and video library for website, social, launches, and authority.",
        includes: [
            "4‑hour photo and video session",
            "1 location or 2 nearby locations",
            "Up to 4 people",
            "Planning call and shot list",
            "Everything in Brand Content Day",
            "40 edited photos",
            "15 social / web crops",
            "1 mini brand video, up to 60 seconds",
            "3 edited vertical videos / reels, 15–30 seconds each",
            "10–15 short raw / social video clips",
            "Simple captions or text overlay if needed",
            "Photo delivery in 10 business days",
            "Video delivery in 14 business days",
        ],
        cta: "Request Photo + Video Package",
        inquiryType: "Summer Brand + Video Day",
    },
];

const EXPERIENCE_STEPS = [
    { n: "01", t: "Quick inquiry", c: "Tell me about your business, what you want to update, and which package you are considering." },
    { n: "02", t: "Recommendation & availability", c: "I reply with the best‑fit package, available dates, and next steps to reserve." },
    { n: "03", t: "Shot list & planning", c: "We create a simple plan so the shoot feels focused, efficient, and calm." },
    { n: "04", t: "Guided session", c: "You do not need to know how to pose. Direction is included throughout." },
    { n: "05", t: "Ready‑to‑use gallery", c: "You receive edited images prepared for website, social media, and marketing use." },
];

const USE_CASES = [
    "Website refresh",
    "Instagram & social content",
    "Profile imagery",
    "Launch visuals",
    "Booking & service pages",
    "LinkedIn & media features",
    "Speaking & press kits",
    "Google Business profile",
    "Email & newsletters",
    "Ads & promos",
];

// ---- Cloudinary helpers for the /business hero + gallery ----
const cldTx = (url, transform) => url.replace("/upload/", `/upload/${transform}/`);
const cldSrcSet = (url, widths = [600, 900, 1200, 1600, 2000]) =>
    widths.map((w) => `${cldTx(url, `f_auto,q_auto,w_${w}`)} ${w}w`).join(", ");

// Phase 10: HERO_VIDEO_* and HERO_POSTER_* consts removed. Hero now uses
// the shared <HeroVideo /> component (which owns these URLs internally),
// so /business and /life-legacy render the same hero treatment from a
// single source. See /app/frontend/src/components/HeroVideo.jsx.

// Selected Business & Brand Work — Phase 5.4: gallery moved to Sanity
// (slug `business-selected-work`). 17 items consumed via
// useBusinessSelectedWork() hook in the component below. The fallback
// (identical 17-item array, same order) lives in `/app/frontend/src/lib/data.js`
// as BUSINESS_GALLERY_ITEMS — preserves the Phase 5 rollback contract.

const FAQS = [
    {
        q: "Do I need to know how to pose?",
        a: "No. You will be guided throughout the session.",
    },
    {
        q: "Can my team be included?",
        a: "Yes. Brand Content Day and Brand Content + Video include up to 4 people.",
    },
    {
        q: "Can we shoot at my office, clinic, or studio?",
        a: "Yes. Most business sessions are designed around your real space.",
    },
    {
        q: "Can I use the photos on my website and social media?",
        a: "Yes. Website and social media usage is included.",
    },
    {
        q: "Is video included?",
        a: "Business Refresh is photo‑only. Brand Content Day includes one edited vertical video. Brand Content + Video includes a mini brand video, edited reels, and short raw / social video clips.",
    },
    {
        q: "How do I reserve a date?",
        a: "A reservation payment and simple agreement are required to hold your date.",
    },
    {
        q: "Are promo prices available forever?",
        a: "No. Promo pricing is available only for bookings reserved by June 18, 2026.",
    },
];

// Note: the original `Placeholder` component was used during early
// development and has been fully removed sitewide. All visible imagery now
// uses real Cloudinary assets from Elizaveta's portfolio.

// ---- PackageCard ---------------------------------------------------------
// Per-card collapsible "Includes" block. On mobile (<768px) the list is
// hidden behind a chevron toggle so multiple cards fit on screen. On desktop
// (>=768px) the list is always visible and the button is inert — preserving
// the existing layout exactly.
function PackageCard({ p }) {
    const [includesOpen, setIncludesOpen] = useState(false);
    const panelId = `pkg-includes-${p.id}`;
    // Each package routes to /inquire with a session-type pre-fill so the
    // Inquire page knows which package the visitor came from. Falls back
    // to a sensible default if a package somehow ships without an
    // inquiryType.
    const inquiryHref = `/inquire?type=${encodeURIComponent(p.inquiryType || `Summer Promo — ${p.name}`)}`;

    return (
        <article
            data-testid={`biz-package-${p.id}`}
            className={cn(
                "relative flex flex-col h-full bg-background border rounded-[3px] p-6 sm:p-7 transition-all",
                p.badge
                    ? "border-[hsl(var(--lux-oxblood))] shadow-[0_24px_60px_-30px_rgba(74,24,32,0.35)] ring-1 ring-[hsl(var(--lux-oxblood))]/15"
                    : "border-border hover:border-foreground/40"
            )}
        >
            {p.badge && (
                <span
                    data-testid={`biz-package-badge-${p.id}`}
                    className="absolute -top-3 left-6 inline-flex items-center bg-[hsl(var(--lux-oxblood))] text-[hsl(var(--lux-porcelain))] text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 rounded-full"
                >
                    {p.badge}
                </span>
            )}
            <h3 className="font-serif-display text-2xl leading-tight mt-1">{p.name}</h3>

            <div className="mt-3 flex items-baseline gap-3">
                <span className="text-[hsl(var(--lux-oxblood))] font-serif-display text-2xl">{p.promo}</span>
                <span className="text-xs tracking-[0.04em] text-muted-foreground line-through">{p.regular}</span>
            </div>
            <div className="mt-1 text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--lux-brass))]">
                Summer promo price
            </div>

            <Separator className="my-5 bg-[hsl(var(--lux-brass))]/45 w-8" />

            <div className="kicker">Best for</div>
            <p className="mt-1.5 text-[13px] leading-[1.7] text-foreground/85">{p.bestFor}</p>

            {/* Includes — mobile collapsible, desktop always open */}
            <div className="mt-5 border-t border-border/60 pt-4 md:border-t-0 md:pt-0">
                <button
                    type="button"
                    onClick={() => setIncludesOpen((v) => !v)}
                    aria-expanded={includesOpen}
                    aria-controls={panelId}
                    data-testid={`biz-package-includes-toggle-${p.id}`}
                    className="flex items-center justify-between w-full text-left md:cursor-default md:pointer-events-none"
                >
                    <span className="kicker">Includes</span>
                    <ChevronDown
                        aria-hidden
                        className={cn(
                            "h-4 w-4 text-muted-foreground transition-transform duration-200 md:hidden",
                            includesOpen && "rotate-180"
                        )}
                    />
                </button>
                <div
                    id={panelId}
                    className={cn(
                        "overflow-hidden transition-[max-height,opacity] duration-200 ease-out",
                        includesOpen ? "max-h-[1200px] opacity-100" : "max-h-0 opacity-0",
                        "md:!max-h-none md:!opacity-100"
                    )}
                >
                    <ul className="mt-3 space-y-2 text-[13px]">
                        {p.includes.map((it) => (
                            <li key={it} className="flex items-start gap-2">
                                <Check className="h-3.5 w-3.5 mt-1.5 text-[hsl(var(--lux-brass))] shrink-0" />
                                <span className="leading-[1.6]">{it}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="mt-auto pt-6">
                <Link
                    to={inquiryHref}
                    onClick={() => {
                        if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "instant" });
                    }}
                    data-testid={`biz-package-cta-${p.id}`}
                    className={cn("btn-request btn-request--sm w-full justify-center", !p.badge && "btn-request--ghost")}
                >
                    {p.cta}
                </Link>
            </div>
        </article>
    );
}



export default function BusinessArizona() {
    const [selectedPkg, setSelectedPkg] = useState("");
    const formRef = useRef(null);
    const packagesRef = useRef(null);
    const GALLERY_ITEMS = useBusinessSelectedWork();

    // Phase 10: responsive hero source state + matchMedia useEffect removed.
    // Hero now uses the shared <HeroVideo /> component which owns that
    // logic internally (single matchMedia listener, mobile/desktop video
    // swap). See /app/frontend/src/components/HeroVideo.jsx.

    // ---- Gallery now uses the unified MediaGallery component (masonry +
    //      PhotoSwipe v5 lightbox + Cloudinary blur-up). The legacy custom
    //      lightbox state and keyboard / body-scroll-lock effects were
    //      removed in favor of PhotoSwipe's built-in handling. -------------

    // Note: page title + meta description are managed by the <Seo />
    // component below (SEO.business). The previous useEffect that
    // imperatively touched document.title is removed to avoid double
    // writes and the missing-dep lint warning.

    const scrollToForm = useCallback((preselect) => {
        if (preselect) setSelectedPkg(preselect);
        const el = formRef.current || (typeof document !== "undefined" && document.getElementById("inquiry"));
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            // Reflect the click in the URL bar so visitors see something
            // happen and so the deep link `/business#inquiry` works for
            // returning users / shared links. `replaceState` avoids
            // pushing a new history entry for an in-page scroll.
            if (typeof window !== "undefined" && window.history?.replaceState) {
                window.history.replaceState(null, "", "#inquiry");
            }
        }
    }, []);

    const scrollToPackages = useCallback(() => {
        const el = packagesRef.current || (typeof document !== "undefined" && document.getElementById("packages"));
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            if (typeof window !== "undefined" && window.history?.replaceState) {
                window.history.replaceState(null, "", "#packages");
            }
        }
    }, []);

    return (
        <div data-testid="business-arizona-page" className="relative">
            <Seo {...SEO.business} />
            {/* ============ HERO ============
              * Phase 10 refactor: previously this rendered its own ~120 LOC
              * inline <video> with scrim layers. Now uses the shared
              * <HeroVideo> component (same one /life-legacy uses), so future
              * hero treatment changes affect both pages from one place.
              * Behavior + visual treatment are identical (autoplay, muted,
              * loop, playsInline + same two-layer scrim). Overlay copy is
              * unchanged.
              */}
            <HeroVideo
                dataTestId="biz-hero"
                ariaLabel="Arizona business photography by Elizaveta von Stuben Imagery — refined editorial visuals for founders, clinics, and creative brands."
            >
                <span
                    className="block font-sans uppercase text-[11px] sm:text-[12px]"
                    style={{
                        color: "#e6e1db",
                        letterSpacing: "0.22em",
                        ...heroTextShadow.light,
                    }}
                >
                    Business &amp; Brand Photography &middot; Arizona
                </span>
                <h1
                    className="font-serif-display mt-3 sm:mt-4 text-[clamp(31px,8vw,33px)] sm:text-[clamp(48px,5.4vw,84px)]"
                    data-testid="biz-hero-headline"
                    style={{
                        color: "#fbf8f4",
                        lineHeight: 1.02,
                        letterSpacing: "0",
                        maxWidth: "760px",
                        ...heroTextShadow.heavy,
                    }}
                >
                    <span className="block">Stop waiting.</span>
                    <span className="block whitespace-nowrap sm:whitespace-normal">Start being seen.</span>
                </h1>
                <p
                    className="mt-4 sm:mt-6 text-[15px] sm:text-[18px] leading-[1.5] sm:leading-[1.55]"
                    style={{
                        color: "#f5f1eb",
                        maxWidth: "620px",
                        ...heroTextShadow.medium,
                    }}
                >
                    Strategic brand photography for entrepreneurs, creatives, personal brands, and growing businesses.
                </p>
                <div className="mt-6 sm:mt-8 flex flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={() => scrollToForm()}
                        data-testid="biz-hero-primary-cta"
                        className="btn-request"
                        style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.35)" }}
                    >
                        Check Summer Availability
                    </button>
                    <button
                        type="button"
                        onClick={scrollToPackages}
                        data-testid="biz-hero-secondary-cta"
                        className="btn-request btn-request--sm btn-request--outline-light"
                        style={{
                            borderColor: "rgba(251,248,244,0.65)",
                            color: "#fbf8f4",
                            boxShadow: "0 6px 22px rgba(0,0,0,0.25)",
                        }}
                    >
                        View Packages
                    </button>
                </div>
                <p
                    className="mt-5 sm:mt-7 font-sans uppercase"
                    data-testid="biz-urgency-line"
                    style={{
                        color: "#d1a86a",
                        fontSize: "12px",
                        letterSpacing: "0.22em",
                        ...heroTextShadow.light,
                    }}
                >
                    {URGENCY_LINE}
                </p>
            </HeroVideo>

            {/* ============ THE OPPORTUNITY ============
                Moved above the gallery: frames the WHY of a planned shoot
                before visual proof, then prices, then qualification.
                Premium-storytelling order per the user's Round 2 brief. */}
            <Section spacing="normal" className="bg-[hsl(var(--lux-porcelain))] border-t border-border" data-testid="biz-opportunity">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-7">
                        <Kicker>The opportunity</Kicker>
                        <SerifTitle className="mt-3">One planned shoot. A clearer way to be seen.</SerifTitle>
                        <p className="mt-5 text-base leading-[1.95] text-foreground/85 max-w-[60ch]">
                            One focused session gives you images for your website, socials, booking pages, launches, and press &mdash; and the quiet confidence of knowing how your brand reads to the people you most want to reach.
                        </p>
                    </div>
                    <ul className="lg:col-span-5 space-y-3" data-testid="biz-benefits">
                        {BENEFITS.map((b) => (
                            <li key={b} className="flex items-start gap-3 bg-background border border-border rounded-[2px] px-5 py-4">
                                <Check className="h-4 w-4 mt-1 text-[hsl(var(--lux-brass))] shrink-0" />
                                <span className="text-sm">{b}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </Section>

            {/* ============ SELECTED BUSINESS & BRAND WORK (GALLERY / PROOF) ============ */}
            <Section spacing="loose" data-testid="biz-gallery">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-10">
                    <div className="lg:col-span-7">
                        <Kicker>Selected work</Kicker>
                        <SerifTitle className="mt-3">Selected Business &amp; Brand Work.</SerifTitle>
                    </div>
                    <p className="lg:col-span-5 text-sm leading-[1.85] text-muted-foreground max-w-[52ch]">
                        Portraits, workspace, and brand details &mdash; images you feel proud to send people to.
                    </p>
                </div>

                {/* Editorial portfolio wall — uses the unified MediaGallery
                    component (masonry on mobile, blur-up placeholders,
                    PhotoSwipe v5 lightbox with swipe / pinch-zoom / keyboard
                    nav). Desktop column count preserved at 3. */}
                <MediaGallery
                    items={GALLERY_ITEMS}
                    desktopColumns={3}
                    initialCount={GALLERY_ITEMS.length}
                    galleryId="biz-gallery-grid"
                    defaultInquiryType="Brand Story Session"
                    className="mx-auto max-w-[1280px]"
                />

                {/* Secondary gallery links */}
                <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-border pt-7">
                    <Link
                        to="/portfolio/brand-business"
                        data-testid="biz-gallery-view-portfolio"
                        className="inline-flex items-center text-xs tracking-[0.18em] uppercase lux-underline"
                    >
                        View Full Business Portfolio <ArrowUpRight className="ml-2 h-4 w-4" />
                    </Link>
                    <Link
                        to="/brand-business"
                        data-testid="biz-gallery-view-brand-business"
                        className="inline-flex items-center text-[11px] tracking-[0.16em] uppercase text-muted-foreground hover:text-foreground transition-colors"
                    >
                        Explore the full Brand &amp; Business studio <ArrowUpRight className="ml-2 h-3.5 w-3.5" />
                    </Link>
                </div>
            </Section>

            {/* ============ PHASE 15: VIDEO GALLERY (auto-hides until populated) ============ *
              * Reads the `business-video` Sanity gallery instance. The
              * section returns null entirely while the doc is unpublished
              * or empty — no orphan eyebrow, no extra spacing. Eli flips
              * `published` + adds ≥1 clip in Studio → section appears
              * here without a code change. */}
            <BusinessVideoSection />

            {/* ============ SUMMER PROMO PACKAGES ============ */}
            <section ref={packagesRef} id="packages" data-testid="biz-packages-section" className="scroll-mt-24 bg-[hsl(var(--lux-porcelain))] border-t border-border">
                <Section spacing="loose">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
                        <div className="lg:col-span-7">
                            <Kicker>Summer Promo · Limited</Kicker>
                            <SerifTitle className="mt-3">Summer Business Visibility Packages.</SerifTitle>
                        </div>
                        <p className="lg:col-span-5 text-sm leading-[1.85] text-muted-foreground max-w-[52ch]">
                            A limited Arizona summer offer with clear deliverables, guided direction, and content you can use right away. Designed to give you a sharper visual direction for your website, socials, and brand presence.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {PACKAGES.map((p) => (
                            <PackageCard key={p.id} p={p} />
                        ))}
                    </div>

                    <p className="mt-8 text-xs text-muted-foreground max-w-[78ch]">
                        Need more reels, interview clips, launch content, or ongoing monthly support? Additional edits can be added once the shoot is planned.
                    </p>

                    <div className="mt-10 flex flex-wrap justify-center gap-3">
                        <button type="button" onClick={() => scrollToForm()} className="btn-request" data-testid="biz-packages-cta">
                            Check Summer Availability <ArrowDown className="ml-2 h-4 w-4" />
                        </button>
                    </div>
                </Section>
            </section>

            {/* ============ WHO THIS IS FOR ============
                Round 2 redesign: compact 2-col (mobile) / 4-col (desktop)
                tile grid. Each tile = short bold label + one-line copy.
                No numbered prefix. Fits in 1–1.5 mobile screens instead of
                the previous 4+. Section heading + location subtitle preserved. */}
            <Section spacing="normal" data-testid="biz-who-this-is-for">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-10">
                    <div className="lg:col-span-7">
                        <Kicker>Who this is for</Kicker>
                        <SerifTitle className="mt-3">For founders, creatives, personal brands, and growing local businesses.</SerifTitle>
                    </div>
                    <p className="lg:col-span-5 text-sm leading-[1.85] text-muted-foreground max-w-[48ch]">
                        Phoenix, Scottsdale, Paradise Valley, Tempe, Mesa, Chandler, and nearby cities.
                    </p>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    {BUYER_CARDS.map((b) => (
                        <article
                            key={b.id}
                            data-testid={`biz-buyer-${b.id}`}
                            className="bg-[hsl(var(--lux-porcelain))] border border-border rounded-[2px] p-4 sm:p-5 flex flex-col h-full"
                        >
                            <h3 className="font-serif-display text-[15px] sm:text-base leading-[1.25] text-[hsl(var(--lux-oxblood))]">
                                {b.title}
                            </h3>
                            <p className="mt-2 text-[12.5px] sm:text-[13px] leading-[1.55] text-muted-foreground">
                                {b.copy}
                            </p>
                        </article>
                    ))}
                </div>
            </Section>

            {/* ============ THE PROBLEM ============
                Round 2: moved below Who Is This For (clustered with the
                other framing sections) so the explicit Hero → Opportunity →
                Gallery → Packages → Who flow stays clean. */}
            <Section spacing="loose" data-testid="biz-problem">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <Kicker>The problem</Kicker>
                        <SerifTitle className="mt-3">Your visuals are already selling before you speak.</SerifTitle>
                        <HairlineBrass className="mt-6" />
                    </div>
                    <p className="lg:col-span-6 lg:col-start-7 text-base leading-[1.95] text-foreground/85 max-w-[60ch]">
                        Before someone reads, books, or hires, they look. If your photos no longer match the work you do or the person you&rsquo;ve become, people hesitate &mdash; and quietly move on.
                    </p>
                </div>
            </Section>

            {/* ============ EXPERIENCE ============ */}
            <Section spacing="normal" data-testid="biz-experience">
                <Kicker>The experience</Kicker>
                <SerifTitle className="mt-3">Calm, guided, and ready to be seen.</SerifTitle>
                <div className="mt-12 grid grid-cols-1 md:grid-cols-5 gap-4 sm:gap-5">
                    {EXPERIENCE_STEPS.map((s) => (
                        <div key={s.n} className="bg-[hsl(var(--lux-porcelain))] border border-border rounded-[2px] p-6 flex flex-col h-full">
                            <div className="kicker">{s.n}</div>
                            <div className="font-serif-display text-lg mt-2 leading-tight">{s.t}</div>
                            <Separator className="my-4 bg-[hsl(var(--lux-brass))]/45 w-8" />
                            <p className="text-[13px] leading-[1.7] text-muted-foreground">{s.c}</p>
                        </div>
                    ))}
                </div>
            </Section>

            {/* ============ USE CASES ============ */}
            <Section spacing="normal" className="bg-[hsl(var(--lux-porcelain))] border-t border-border" data-testid="biz-use-cases">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-10">
                    <div className="lg:col-span-7">
                        <Kicker>Practical value</Kicker>
                        <SerifTitle className="mt-3">Content that works everywhere people find you.</SerifTitle>
                    </div>
                    <p className="lg:col-span-5 text-sm leading-[1.85] text-muted-foreground max-w-[48ch]">
                        Planned around how you actually show up &mdash; website, social, launches, and the rooms you walk into.
                    </p>
                </div>
                <ul className="flex flex-wrap gap-2.5">
                    {USE_CASES.map((u) => (
                        <li
                            key={u}
                            className="px-4 h-10 inline-flex items-center rounded-full border border-border bg-background text-[12px] tracking-[0.04em] text-foreground/85"
                        >
                            {u}
                        </li>
                    ))}
                </ul>
            </Section>

            {/* ============ PHASE 12: FOR CLINICS TILE ============ *
              *
              * Compact link to the dedicated /aesthetic-clinic-photography
              * campaign landing page. Inserted between Use Cases and
              * Selected Client Work per Phase 12 spec. Intentionally
              * monochrome and bordered so it reads as an "additional
              * offering for a specific audience," not a primary product —
              * does not compete with the bundles or industry tiles above.
              */}
            <Section spacing="tight" data-testid="biz-clinic-tile">
                <div className="border border-border rounded-[3px] bg-background p-6 sm:p-8 lg:p-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start lg:items-center">
                        <div className="lg:col-span-8">
                            <Kicker>For clinics</Kicker>
                            <h3 className="font-serif-display text-2xl sm:text-[28px] lg:text-[32px] leading-[1.15] mt-3">
                                Medical, aesthetic, and wellness practices
                            </h3>
                            <p className="mt-4 text-sm sm:text-[15px] leading-[1.8] text-foreground/80 max-w-[64ch]">
                                Provider portraits, polished space details,
                                service visuals, and short clips for
                                website, Google, booking pages, and social.
                            </p>
                        </div>
                        <div className="lg:col-span-4 lg:justify-self-end w-full lg:w-auto">
                            <Link
                                to="/aesthetic-clinic-photography"
                                data-testid="biz-clinic-tile-cta"
                                className="btn-request btn-request--sm btn-request--ghost w-full lg:w-auto justify-center inline-flex"
                            >
                                Clinic Content Sessions
                            </Link>
                        </div>
                    </div>
                </div>
            </Section>

            {/* ============ HOME-SERVICES TILE ============ *
              *
              * Compact link to the dedicated /home-services-photography
              * campaign landing page (HVAC, plumbing, electrical, roofing,
              * and home-service companies in Arizona). Same monochrome /
              * bordered treatment as the clinic tile above — reads as an
              * "additional offering for a specific audience" so it doesn't
              * compete with the bundles or industry tiles. Sits between
              * Use Cases and Selected Client Work, after the clinic tile.
              */}
            <Section spacing="tight" data-testid="biz-home-services-tile">
                <div className="border border-border rounded-[3px] bg-background p-6 sm:p-8 lg:p-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start lg:items-center">
                        <div className="lg:col-span-8">
                            <Kicker>For home-service companies</Kicker>
                            <h3 className="font-serif-display text-2xl sm:text-[28px] lg:text-[32px] leading-[1.15] mt-3">
                                HVAC, plumbing, electrical &amp; home services
                            </h3>
                            <p className="mt-4 text-sm sm:text-[15px] leading-[1.8] text-foreground/80 max-w-[64ch]">
                                Team and truck portraits, finished-work
                                and before/after shots, and short clips
                                for your website, Google profile, ads,
                                and social.
                            </p>
                        </div>
                        <div className="lg:col-span-4 lg:justify-self-end w-full lg:w-auto">
                            <Link
                                to="/home-services-photography"
                                data-testid="biz-home-services-tile-cta"
                                className="btn-request btn-request--sm btn-request--ghost w-full lg:w-auto justify-center inline-flex"
                            >
                                Home-Service Content Sessions
                            </Link>
                        </div>
                    </div>
                </div>
            </Section>

            {/* ============ PHASE 9: SELECTED CLIENT WORK ============ *
              *
              * Quiet credibility strip. Inserted between Use Cases and
              * Testimonials per Phase 9 sign-off: breadth (logos) → depth
              * (quotes) → handoff (Why Elizaveta) → action (form). Pulls
              * SELECTED_CLIENTS from data.js. Logos rendered in monochrome
              * if URLs are present; wordmark tiles otherwise.
              */}
            <SelectedClientWork
                spacing="normal"
                testId="biz-selected-clients"
            />

            {/* ============ PHASE 9: TESTIMONIALS ============ *
              *
              * Business-tagged subset only (useTestimonials('business')).
              * Cards use the shared TestimonialCard with backdrop-image
              * support: full-bleed photo + gradient overlay on ≥640px,
              * framed crop above quote on mobile. Each card auto-switches
              * to the empty-state layout if its backdropImage isn't
              * populated in Sanity yet.
              *
              * Section auto-hides if no business-tagged testimonials
              * exist (handles transition window cleanly).
              */}
            <BusinessTestimonialsSection />

            {/* ============ WHY WORK WITH ELIZAVETA ============ */}
            <Section spacing="normal" data-testid="biz-why-elizaveta">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <Kicker>Why work with Elizaveta</Kicker>
                        <SerifTitle className="mt-3">Visuals with an editorial eye and a business purpose.</SerifTitle>
                        <HairlineBrass className="mt-6" />
                        <p className="mt-6 text-base leading-[1.95] text-foreground/85 max-w-[58ch]">
                            Elizaveta brings an artistic, refined approach to business photography, creating images that feel polished without looking generic. The goal is to make your business feel elevated, trustworthy, and human — while giving you practical content you can actually use.
                        </p>
                    </div>
                    <div className="lg:col-span-5">
                        <figure className="relative overflow-hidden rounded-[3px] border border-border bg-[hsl(var(--lux-porcelain))]" style={{ aspectRatio: "4/5" }}>
                            <img
                                src="https://res.cloudinary.com/dub8rhyez/image/upload/f_auto,q_auto,w_1200/_____Businesscreative_portfolio_evs-7_kykjzs.jpg"
                                srcSet={cldSrcSet("https://res.cloudinary.com/dub8rhyez/image/upload/_____Businesscreative_portfolio_evs-7_kykjzs.jpg")}
                                sizes="(max-width: 1024px) 90vw, 40vw"
                                alt="Creative founder brand imagery by Elizaveta von Stuben — refined visual direction for Arizona founders and personal brands."
                                loading="lazy"
                                decoding="async"
                                className="absolute inset-0 h-full w-full object-cover"
                                style={{ objectPosition: "center 25%" }}
                            />
                        </figure>
                    </div>
                </div>
            </Section>

            {/* ============ FINAL CTA / INQUIRY FORM ============ */}
            <section ref={formRef} id="inquiry" data-testid="biz-form-section" data-sticky-cta-hide="true" className="scroll-mt-24 border-t border-border">
                <Section spacing="loose">
                    <div className="max-w-[820px] mx-auto text-center">
                        <Kicker>Check Summer Availability</Kicker>
                        <SerifTitle className="mt-3">Tell me about your business.</SerifTitle>
                        <p className="mt-5 text-sm text-muted-foreground max-w-[60ch] mx-auto">
                            Tell me a little about your business and the package you are considering. I will reply with available dates, the best‑fit option, and next steps to reserve.
                        </p>
                    </div>

                    <BusinessInquiryForm selectedPkg={selectedPkg} onSelectedPkgChange={setSelectedPkg} />
                </Section>
            </section>

            {/* ============ FAQ (after the form per the conversion hierarchy:
                 hero → gallery → packages → framing → form captures lead →
                 FAQ resolves any last-mile objections) ============ */}
            <Section spacing="normal" className="bg-[hsl(var(--lux-porcelain))] border-t border-border" data-testid="biz-faq">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <Kicker>FAQ</Kicker>
                        <SerifTitle className="mt-3">Good questions, clear answers.</SerifTitle>
                        <p className="mt-5 text-sm text-muted-foreground max-w-[40ch]">
                            Anything not answered here? Mention it in your inquiry and I will address it in my reply.
                        </p>
                    </div>
                    <div className="lg:col-span-8">
                        <Accordion type="single" collapsible className="bg-background border border-border rounded-[3px]">
                            {FAQS.map((f, i) => (
                                <AccordionItem key={f.q} value={`q${i}`} className="border-b border-border last:border-b-0 px-5">
                                    <AccordionTrigger
                                        data-testid={`biz-faq-trigger-${i}`}
                                        className="text-left text-[15px] font-serif-display leading-snug hover:no-underline"
                                    >
                                        {f.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-sm text-muted-foreground leading-[1.85] pb-5">
                                        {f.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </Section>

            {/* ============ SECONDARY GALLERY LINKS were merged into the
                 MediaGallery section above; lightbox is now handled by the
                 component itself (PhotoSwipe v5). ============ */}
        </div>
    );
}

// ===========================================================
// FORM
// ===========================================================

// ──────────────────────────────────────────────────────────────────────
// BusinessTestimonialsSection — Phase 9 social-proof addition.
//
// Renders business-tagged testimonials on /business between the Selected
// Client Work strip and the Why Elizaveta section. Reuses the shared
// <TestimonialCard /> from Reviews so the backdrop + overlay treatment
// is identical across both pages.
//
// Empty-state handling: useTestimonials('business') returns [] when zero
// business-tagged docs exist in Sanity (the empty-array path was added
// in Phase 9 specifically for this case). When empty, the section
// renders nothing — keeps the page clean during the transition window
// before testimonials are tagged 'business' in Studio.
// ──────────────────────────────────────────────────────────────────────
// Phase 15 — slug-driven /business video section. Auto-hides until the
// `business-video` Sanity instance is published with ≥1 item.
function BusinessVideoSection() {
    const items = useBusinessVideo();
    return (
        <VideoGallerySection
            items={items}
            testId="biz-video"
            galleryId="biz-video-gallery"
            inquiryType="Business Brand Video"
            className="bg-[hsl(var(--lux-porcelain))] border-t border-border"
        />
    );
}


function BusinessTestimonialsSection() {
    const businessReviews = useTestimonials("business");
    if (!businessReviews || businessReviews.length === 0) return null;

    return (
        <section
            id="biz-testimonials"
            data-testid="biz-testimonials"
            className="scroll-mt-24 border-t border-border"
        >
            <Section spacing="normal">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-10 sm:mb-12">
                    <div className="lg:col-span-7">
                        <Kicker>What clients say</Kicker>
                        <SerifTitle className="mt-3">
                            Calm, professional, and on brand.
                        </SerifTitle>
                    </div>
                    <p className="lg:col-span-5 text-sm leading-[1.85] text-muted-foreground max-w-[52ch]">
                        A small set of clients who&apos;ve worked with the
                        studio for brand and event photography &mdash; in their
                        own words.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                    {businessReviews.map((r) => (
                        <TestimonialCard key={r.id} testimonial={r} />
                    ))}
                </div>
            </Section>
        </section>
    );
}

function BusinessInquiryForm({ selectedPkg, onSelectedPkgChange }) {    const [form, setForm] = useState({
        full_name: "",
        business_name: "",
        email: "",
        phone: "",
        website_or_instagram: "",
        package_interest: "",
        desired_date: "",
        updates_needed: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // Sync external package selection (from package CTAs) into the form.
    // Uses functional setState so we don't need `form.package_interest` in
    // deps; the no-op branch keeps the same object identity when there's
    // nothing to update, so React skips the re-render.
    useEffect(() => {
        if (!selectedPkg) return;
        setForm((f) => (f.package_interest === selectedPkg ? f : { ...f, package_interest: selectedPkg }));
    }, [selectedPkg]);

    const onChange = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.full_name || !form.email) {
            toast.error("Please add your name and email.");
            return;
        }
        if (!form.package_interest) {
            toast.error("Please choose a package option.");
            return;
        }

        // Pack the business-specific fields into the existing /api/inquiries
        // shape so leads are captured without any backend schema change.
        const visionLines = [
            form.business_name && `Business: ${form.business_name}`,
            form.website_or_instagram && `Website / IG: ${form.website_or_instagram}`,
            form.updates_needed && `What to update: ${form.updates_needed}`,
            form.message && `Message: ${form.message}`,
            "Source: /business (Arizona Summer Promo)",
        ].filter(Boolean).join("\n");

        const payload = {
            full_name: form.full_name,
            email: form.email,
            phone: form.phone || null,
            session_type: form.package_interest,
            desired_date: form.desired_date || null,
            location: "Scottsdale / Phoenix",
            vision: visionLines,
            inspiration_url: form.website_or_instagram || null,
            hear_about: "Business landing page",
        };

        try {
            setSubmitting(true);
            await PublicAPI.createInquiry(payload);
            setSubmitted(true);
            toast.success("Inquiry received — you'll hear back within 24 hours.");
            window.scrollTo({ top: window.scrollY - 60, behavior: "smooth" });
        } catch (err) {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div
                data-testid="biz-form-success"
                className="max-w-[760px] mx-auto mt-10 border border-[hsl(var(--lux-brass))]/40 bg-[hsl(var(--lux-porcelain))] p-10 sm:p-14 rounded-[3px] text-center"
            >
                <Kicker>Thank you</Kicker>
                <SerifTitle className="mt-3">Your inquiry has been sent.</SerifTitle>
                <p className="mt-5 text-sm text-muted-foreground max-w-[60ch] mx-auto">
                    I will reply with availability and package recommendations within 24 hours.
                </p>
            </div>
        );
    }

    return (
        <form
            data-testid="biz-form"
            onSubmit={submit}
            className="max-w-[820px] mx-auto mt-10 bg-[hsl(var(--lux-porcelain))] border border-border rounded-[3px] p-7 sm:p-10"
        >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Name" required htmlFor="biz-full_name">
                    <Input id="biz-full_name" name="full_name" autoComplete="name" data-testid="biz-field-name" required value={form.full_name} onChange={onChange("full_name")} />
                </Field>
                <Field label="Business name" htmlFor="biz-business_name">
                    <Input id="biz-business_name" name="business_name" autoComplete="organization" data-testid="biz-field-business" value={form.business_name} onChange={onChange("business_name")} />
                </Field>
                <Field label="Email" required htmlFor="biz-email">
                    <Input id="biz-email" name="email" type="email" autoComplete="email" data-testid="biz-field-email" required value={form.email} onChange={onChange("email")} />
                </Field>
                <Field label="Phone (optional)" htmlFor="biz-phone">
                    <Input id="biz-phone" name="phone" type="tel" autoComplete="tel" data-testid="biz-field-phone" value={form.phone} onChange={onChange("phone")} />
                </Field>
                <Field label="Website or Instagram" className="sm:col-span-2" htmlFor="biz-website_or_instagram">
                    <Input
                        id="biz-website_or_instagram"
                        name="website_or_instagram"
                        autoComplete="url"
                        inputMode="url"
                        data-testid="biz-field-web-ig"
                        placeholder="https:// or @handle"
                        value={form.website_or_instagram}
                        onChange={onChange("website_or_instagram")}
                    />
                </Field>
                <Field label="Package interested in" required htmlFor="biz-package_interest">
                    <Select
                        value={form.package_interest}
                        onValueChange={(v) => {
                            setForm({ ...form, package_interest: v });
                            onSelectedPkgChange?.(v);
                        }}
                        name="package_interest"
                    >
                        <SelectTrigger id="biz-package_interest" data-testid="biz-field-package"><SelectValue placeholder="Choose one" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="Business Refresh">Business Refresh</SelectItem>
                            <SelectItem value="Brand Content Day">Brand Content Day</SelectItem>
                            <SelectItem value="Brand Content + Video">Brand Content + Video</SelectItem>
                            <SelectItem value="Not sure">Not sure</SelectItem>
                        </SelectContent>
                    </Select>
                </Field>
                <Field label="Preferred shoot month or ideal dates" htmlFor="biz-desired_date">
                    <Input
                        id="biz-desired_date"
                        name="desired_date"
                        data-testid="biz-field-date"
                        placeholder="Example: June, mid‑July, flexible"
                        value={form.desired_date}
                        onChange={onChange("desired_date")}
                    />
                </Field>
            </div>

            <div className="mt-5">
                <Field label="What do you want to update?" htmlFor="biz-updates_needed">
                    <Input
                        id="biz-updates_needed"
                        name="updates_needed"
                        data-testid="biz-field-updates"
                        placeholder="Website, social, team photos, video, launch, Google profile, ads…"
                        value={form.updates_needed}
                        onChange={onChange("updates_needed")}
                    />
                </Field>
            </div>

            <div className="mt-5">
                <Field label="Short message" htmlFor="biz-message">
                    <Textarea
                        id="biz-message"
                        name="message"
                        data-testid="biz-field-message"
                        rows={4}
                        placeholder="Tell me a little about your business, your goals, and where you want to use these images."
                        value={form.message}
                        onChange={onChange("message")}
                    />
                </Field>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-xs text-muted-foreground max-w-[44ch]">
                    Summer promo pricing is available for bookings reserved by June 18, 2026.
                </p>
                <button
                    data-testid="biz-form-submit"
                    type="submit"
                    disabled={submitting}
                    className="btn-request"
                >
                    {submitting ? "Sending…" : "Check Availability"}
                </button>
            </div>
        </form>
    );
}

const Field = ({ label, children, required, className, htmlFor }) => (
    <div className={cn("block", className)}>
        <label htmlFor={htmlFor} className="kicker block mb-2 cursor-pointer">
            {label}
            {required && <span className="ml-1 text-[hsl(var(--lux-oxblood))]">*</span>}
        </label>
        {children}
    </div>
);
